// Browsing an end-to-end encrypted git repository on this device (HRTH-114, docs/STORAGE.md
// §4.3): refs are decrypted (and checked against their blinded ids), packs are downloaded,
// decrypted and kept on the device, and the Rust core (WASM) reads commits, trees, history
// and diffs from them. The server never sees any of it in the clear.

import { signedGet } from './sealedStorage'
import type { LibraryEntry, RepoRef } from '../api/types'
import { GitRepo } from '../wasm/pkg/hrth.js'
import { decryptAll, type MediaKeyInfo } from './attachments'
import { blindRef, decryptName } from './metacrypto'

export interface GitPerson {
  name: string
  email: string
  /** Seconds since the epoch. */
  time: number
  tz: string
}

export interface GitCommit {
  id: string
  tree: string
  parents: string[]
  author: GitPerson
  committer: GitPerson
  message: string
}

export type GitEntryKind = 'file' | 'dir' | 'symlink' | 'submodule'

export interface GitTreeEntry {
  name: string
  mode: string
  id: string
  kind: GitEntryKind
}

export interface GitFileDiff {
  path: string
  change: 'added' | 'deleted' | 'modified'
  binary: boolean
  /** Unified diff text (empty for binary or very large files). */
  patch: string
  additions: number
  deletions: number
}

/** A branch or tag, decrypted. */
export interface RefTarget {
  name: string
  sha: string
}

/** Typed access to the WASM repository reader. */
export class Repository {
  private raw = new GitRepo()

  addPack(pack: Uint8Array): number {
    return this.raw.addPack(pack)
  }
  has(id: string): boolean {
    return this.raw.has(id)
  }
  commit(id: string): GitCommit {
    return this.raw.commit(id) as GitCommit
  }
  tree(id: string): GitTreeEntry[] {
    return this.raw.tree(id) as GitTreeEntry[]
  }
  entryAt(commit: string, path: string): GitTreeEntry {
    return this.raw.entryAt(commit, path) as GitTreeEntry
  }
  blob(id: string): Uint8Array {
    return this.raw.blob(id)
  }
  log(start: string, limit: number, path?: string): GitCommit[] {
    return this.raw.log(start, limit, path ?? undefined) as GitCommit[]
  }
  diffCommit(id: string): GitFileDiff[] {
    return this.raw.diffCommit(id) as GitFileDiff[]
  }
  diffBetween(from: string, to: string): GitFileDiff[] {
    return this.raw.diffBetween(from, to) as GitFileDiff[]
  }
  mergeBase(a: string, b: string): string | undefined {
    return this.raw.mergeBase(a, b) ?? undefined
  }
  isAncestor(a: string, b: string): boolean {
    return this.raw.isAncestor(a, b)
  }
  /** `{kind: up_to_date | fast_forward | commit | conflicts, commit?, paths?}`; see `takePack`. */
  merge(target: string, source: string, message: string, name: string, email: string, time: number) {
    return this.raw.merge(target, source, message, name, email, time) as
      | { kind: 'up_to_date' }
      | { kind: 'fast_forward'; commit: string }
      | { kind: 'commit'; commit: string }
      | { kind: 'conflicts'; paths: string[] }
  }
  /** The pack of new objects from the last merge commit. */
  takePack(): Uint8Array {
    return this.raw.takePack()
  }
  free(): void {
    this.raw.free()
  }
}

/** The repository's branches and tags, decrypted; ones that don't check out are left out. */
export async function decryptRefs(key: string, library: string, refs: RepoRef[]): Promise<RefTarget[]> {
  const out: RefTarget[] = []
  for (const r of refs) {
    if (!r.target) continue
    const json = await decryptName(key, `repo-ref:${library}`, r.target)
    if (!json || json === r.target) continue
    try {
      const t = JSON.parse(json) as RefTarget
      // A target only counts under the id its name blinds to, so one can't be moved.
      if (typeof t.name === 'string' && /^[0-9a-f]{40}$/.test(t.sha) && (await blindRef(key, library, t.name)) === r.id) out.push(t)
    } catch {
      // not a ref target
    }
  }
  return out.sort((a, b) => a.name.localeCompare(b.name))
}

/** The branch to show first: main, else master, else the first branch. */
export function defaultBranch(refs: RefTarget[]): RefTarget | undefined {
  const heads = refs.filter((r) => r.name.startsWith('refs/heads/'))
  return heads.find((r) => r.name === 'refs/heads/main') ?? heads.find((r) => r.name === 'refs/heads/master') ?? heads[0]
}

interface PackMeta {
  kind: string
  info: MediaKeyInfo
}

/** Where decrypted packs are kept on the device, so later visits fetch only new ones. */
export interface PackCache {
  getKv<T>(key: string): Promise<T | undefined>
  setKv(key: string, value: unknown): Promise<void>
}

export interface PackApi {
  libraryEntries(library: string): Promise<{ entries: LibraryEntry[] }>
  /** `signed`: a GET signed here, for a repository in a sealed bucket (the server relays it). */
  blobUrl(blob: string, signed?: string): Promise<{ url: string }>
}

/**
 * Loads every pack of a repository into `repo`: from the device's cache when it has one,
 * otherwise downloaded and decrypted (then cached). Returns how many were downloaded.
 */
export async function loadPacks(
  api: PackApi,
  library: string,
  key: string,
  repo: Repository,
  cache: PackCache | null,
  fetchBytes: (url: string) => Promise<ArrayBuffer> = async (url) => {
    const r = await fetch(url)
    if (!r.ok) throw new Error(`couldn't download a pack (${r.status})`)
    return r.arrayBuffer()
  },
): Promise<number> {
  const { entries } = await api.libraryEntries(library)
  let downloaded = 0
  for (const e of entries) {
    const cacheKey = `gitpack:${library}:${e.id}`
    let pack = await cache?.getKv<Uint8Array>(cacheKey)
    if (!pack) {
      const json = await decryptName(key, `library-entry:${library}`, e.meta)
      if (!json || json === e.meta) continue
      const meta = JSON.parse(json) as PackMeta
      const blob = e.blobs.find((b) => b.role === 'original')
      if (meta.kind !== 'git-pack' || !blob) continue // not a pack, or a push still uploading
      pack = await decryptAll(meta.info, await fetchBytes((await api.blobUrl(blob.id, signedGet(blob.id, library))).url))
      await cache?.setKv(cacheKey, pack)
      downloaded++
    }
    repo.addPack(pack)
  }
  return downloaded
}

/** Whether bytes look like text worth showing (no NUL in the first 8000 bytes). */
export function isText(bytes: Uint8Array): boolean {
  return !bytes.subarray(0, 8000).includes(0)
}

/** The first line of a commit message. */
export function subject(c: GitCommit): string {
  return c.message.split('\n', 1)[0] ?? ''
}

export const shortSha = (id: string) => id.slice(0, 7)

/**
 * A repository's name as the folder `git clone` makes: letters, digits, `.`, `-` and `_`,
 * other runs of characters as one `-` (same as the hrth CLI's `folder_name`).
 */
export function folderName(name: string): string {
  let out = ''
  for (const c of name.trim()) {
    if (/[\p{L}\p{N}._-]/u.test(c)) out += c
    else if (!out.endsWith('-')) out += '-'
  }
  out = out.replace(/^[-.]+|[-.]+$/g, '')
  return out || 'repository'
}

/** The URL to clone a repository with: the id finds it, the name names the folder. */
export function cloneUrl(host: string, library: string, name?: string | null): string {
  return name ? `hrth://${host}/${library}/${folderName(name)}` : `hrth://${host}/${library}`
}
